/**
 * Theme system (OD-11, ADR-014): Dark (default) / Light / System.
 * The initial attribute is set before first paint by @hopex/tokens/theme-init.js; this provider keeps
 * React in sync, persists the choice (UI preference only) and resolves "system" for components that
 * need the effective theme. Logged-in persistence to the server arrives with auth (Phase 3).
 */
import {
  DEFAULT_THEME,
  isThemePreference,
  THEME_STORAGE_KEY,
  type ThemeName,
  type ThemePreference,
} from '@hopex/tokens';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useSyncExternalStore,
  type ReactNode,
} from 'react';

interface ThemeContextValue {
  preference: ThemePreference;
  resolved: ThemeName;
  setPreference: (p: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

// The <html data-theme> attribute (set before first paint by theme-init.js) is the single source of
// truth. React subscribes to it with useSyncExternalStore, so server-rendered islands hydrate with the
// default and then re-render to the stored choice without a hydration mismatch.
function subscribePreference(onChange: () => void): () => void {
  const obs = new MutationObserver(onChange);
  obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
  return () => obs.disconnect();
}
function readPreference(): ThemePreference {
  const attr = document.documentElement.getAttribute('data-theme');
  return isThemePreference(attr) ? attr : DEFAULT_THEME;
}

const LIGHT_QUERY = '(prefers-color-scheme: light)';
function subscribeSystem(onChange: () => void): () => void {
  const mq = window.matchMedia(LIGHT_QUERY);
  mq.addEventListener('change', onChange);
  return () => mq.removeEventListener('change', onChange);
}
function readSystem(): ThemeName {
  return window.matchMedia(LIGHT_QUERY).matches ? 'light' : 'dark';
}
const serverPreference = (): ThemePreference => DEFAULT_THEME;
const serverSystem = (): ThemeName => 'dark';

export function ThemeProvider({ children }: { children: ReactNode }) {
  const preference = useSyncExternalStore(subscribePreference, readPreference, serverPreference);
  const system = useSyncExternalStore(subscribeSystem, readSystem, serverSystem);

  const setPreference = useCallback((p: ThemePreference) => {
    const root = document.documentElement;
    root.classList.add('hx-theme-transition');
    root.setAttribute('data-theme', p);
    window.setTimeout(() => root.classList.remove('hx-theme-transition'), 260);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, p);
    } catch {
      /* storage unavailable: the choice lasts for this page only */
    }
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ preference, resolved: preference === 'system' ? system : preference, setPreference }),
    [preference, system, setPreference],
  );
  return <ThemeContext value={value}>{children}</ThemeContext>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}
